import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import apiService, { ServiceType } from '../../services/api';
import type {
    ProfitSummaryDto,
    PerformanceMetricsDto,
    MddResponseDto,
    SharpeResponseDto,
} from '../../api-schema/generated/types';
import { Card, Section } from '../../pages/PairTrading/styles';
import { formatNumber, formatDecimal } from '../../utils/numberFormat';

const coreService = apiService.getService(ServiceType.CORE);

// Cards wrap to the next line before a number gets too narrow to read.
const PerformanceRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 8px;
    margin-bottom: 8px;
    width: 100%;
`;

const MetricCard = styled(Card)`
    min-width: 0;
    width: 100%;
    .ant-card-body { padding: 12px; }
`;

const MetricLabel = styled.div`
    font-size: 11px;
    color: #848E9C;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const MetricValue = styled.div<{ $positive?: boolean }>`
    font-size: 20px;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
    color: ${({ $positive }) => ($positive === undefined ? '#EAECEF' : $positive ? '#0ECB81' : '#F6465D')};
`;

const MetricDetail = styled.div`
    font-size: 12px;
    color: #848E9C;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #2B3139;
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 4px;
    & > span:first-child { flex-shrink: 0; }
    & > span:last-child { text-align: right; min-width: 0; overflow-wrap: anywhere; }
    &:last-child { margin-bottom: 0; }
`;

/** Browser-locale dates read 8/9 as Aug 9 or 8 Sep depending on who looks; this does not. */
function shortDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    // numberFormat-exempt: date
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function isSharpeDto(v: unknown): v is SharpeResponseDto {
    return typeof v === 'object' && v !== null && 'sharpe_ratio' in v;
}

function isMddDto(v: unknown): v is MddResponseDto {
    return typeof v === 'object' && v !== null && 'max_drawdown_pct' in v;
}

interface Props {
    portfolioId: string | null | undefined;
    /** Fetch only while the tab is on screen. */
    active?: boolean;
}

/** Account-wide P&L, Sharpe and MDD: every strategy on the portfolio counts in them. */
const PerformanceMetrics: React.FC<Props> = ({ portfolioId, active = true }) => {
    const [profit, setProfit] = useState<ProfitSummaryDto | null>(null);
    const [metrics, setMetrics] = useState<PerformanceMetricsDto | null>(null);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setProfit(null);
        setMetrics(null);
        setError(null);
        if (!portfolioId) return;
        try {
            const [profitRes, metricsRes] = await Promise.all([
                coreService.get<ProfitSummaryDto>('/performance/profit', { params: { portfolioId } }),
                coreService.get<PerformanceMetricsDto>('/performance/metrics', { params: { portfolioId } }),
            ]);
            setProfit(profitRes.data);
            setMetrics(metricsRes.data);
        } catch (err: any) {
            console.error('Error fetching performance:', err);
            setError(err.response?.data?.message || 'Failed to load performance');
        }
    }, [portfolioId]);

    useEffect(() => {
        if (active) void fetchData();
    }, [active, fetchData]);

    if (!portfolioId) {
        return <div style={{ textAlign: 'center', padding: 24, color: '#848E9C' }}>Please select a portfolio to view performance</div>;
    }
    if (error) {
        return <div style={{ textAlign: 'center', padding: 24, color: '#F6465D' }}>{error}</div>;
    }

    const sharpe = metrics?.sharpe != null && isSharpeDto(metrics.sharpe) ? metrics.sharpe : null;
    const mdd = metrics?.mdd != null && isMddDto(metrics.mdd) ? metrics.mdd : null;

    return (
        <>
            <Section style={{ marginBottom: 8, width: '100%' }}>
                <PerformanceRow>
                    {profit && (
                        <MetricCard>
                            <MetricLabel>Intraday P&L</MetricLabel>
                            <MetricValue $positive={(profit.intradayTotalPnL ?? 0) >= 0}>
                                ₫{formatNumber(profit.intradayTotalPnL ?? 0)}
                            </MetricValue>
                            <MetricDetail>
                                <DetailRow>
                                    <span style={{ color: '#848E9C' }}>Realized</span>
                                    <span style={{ fontWeight: 600, color: (profit.intradayRealizedPnL ?? 0) >= 0 ? '#0ECB81' : '#F6465D' }}>
                                        ₫{formatNumber(profit.intradayRealizedPnL ?? 0)}
                                    </span>
                                </DetailRow>
                                <DetailRow>
                                    <span style={{ color: '#848E9C' }}>Unrealized</span>
                                    <span style={{ fontWeight: 600, color: (profit.intradayUnrealizedPnL ?? 0) >= 0 ? '#0ECB81' : '#F6465D' }}>
                                        ₫{formatNumber(profit.intradayUnrealizedPnL ?? 0)}
                                    </span>
                                </DetailRow>
                            </MetricDetail>
                        </MetricCard>
                    )}
                    {profit && (
                        <MetricCard>
                            <MetricLabel>Total P&L</MetricLabel>
                            <MetricValue $positive={(profit.currentTotalPnL ?? 0) >= 0}>
                                ₫{formatNumber(profit.currentTotalPnL ?? 0)}
                            </MetricValue>
                            <MetricDetail>
                                <DetailRow>
                                    <span style={{ color: '#848E9C' }}>Realized</span>
                                    <span style={{ fontWeight: 600, color: (profit.currentTotalRealized ?? 0) >= 0 ? '#0ECB81' : '#F6465D' }}>
                                        ₫{formatNumber(profit.currentTotalRealized ?? 0)}
                                    </span>
                                </DetailRow>
                                <DetailRow>
                                    <span style={{ color: '#848E9C' }}>Unrealized</span>
                                    <span style={{ fontWeight: 600, color: (profit.currentTotalUnrealized ?? 0) >= 0 ? '#0ECB81' : '#F6465D' }}>
                                        ₫{formatNumber(profit.currentTotalUnrealized ?? 0)}
                                    </span>
                                </DetailRow>
                            </MetricDetail>
                        </MetricCard>
                    )}
                    <MetricCard>
                        <MetricLabel>Sharpe Ratio</MetricLabel>
                        {sharpe ? (
                            <>
                                <MetricValue $positive={sharpe.sharpe_ratio != null && sharpe.sharpe_ratio > 1}>
                                    {formatDecimal(sharpe.sharpe_ratio ?? 0, 3)}
                                </MetricValue>
                                <MetricDetail>
                                    <DetailRow>
                                        <span style={{ color: '#848E9C' }}>Ann. Return</span>
                                        <span style={{ fontWeight: 600, color: (sharpe.annualized_return ?? 0) >= 0 ? '#0ECB81' : '#F6465D' }}>{formatDecimal(sharpe.annualized_return ?? 0, 2)}%</span>
                                    </DetailRow>
                                    <DetailRow>
                                        <span style={{ color: '#848E9C' }}>Ann. Vol</span>
                                        <span style={{ fontWeight: 600, color: '#EAECEF' }}>{formatDecimal(sharpe.annualized_volatility ?? 0, 2)}%</span>
                                    </DetailRow>
                                </MetricDetail>
                            </>
                        ) : (
                            <div style={{ padding: 12, color: '#848E9C', fontSize: 13 }}>No data</div>
                        )}
                    </MetricCard>
                    <MetricCard>
                        <MetricLabel>Maximum Drawdown (MDD)</MetricLabel>
                        {mdd ? (
                            <>
                                <MetricValue $positive={false}>{formatDecimal(mdd.max_drawdown_pct ?? 0, 2)}%</MetricValue>
                                <MetricDetail>
                                    <DetailRow>
                                        <span style={{ color: '#848E9C' }}>Peak</span>
                                        <span style={{ fontWeight: 600, color: '#EAECEF' }}>{shortDate(mdd.peak_date)}</span>
                                    </DetailRow>
                                    <DetailRow>
                                        <span style={{ color: '#848E9C' }}>Trough</span>
                                        <span style={{ fontWeight: 600, color: '#EAECEF' }}>{shortDate(mdd.trough_date)}</span>
                                    </DetailRow>
                                    <DetailRow>
                                        <span style={{ color: '#848E9C' }}>Peak NAV</span>
                                        <span style={{ fontWeight: 600, color: '#0ECB81' }}>₫{formatNumber(mdd.peak_nav ?? 0)}</span>
                                    </DetailRow>
                                    <DetailRow>
                                        <span style={{ color: '#848E9C' }}>Trough NAV</span>
                                        <span style={{ fontWeight: 600, color: '#F6465D' }}>₫{formatNumber(mdd.trough_nav ?? 0)}</span>
                                    </DetailRow>
                                </MetricDetail>
                            </>
                        ) : (
                            <div style={{ padding: 12, color: '#848E9C', fontSize: 13 }}>No data</div>
                        )}
                    </MetricCard>
                </PerformanceRow>
            </Section>

        </>
    );
};

export default PerformanceMetrics;
